import { Injectable, NgZone } from "@angular/core";
import { EMPTY, Subject } from "rxjs";
import { FileAttachment } from "ng/src/app/notes/shared/file-attachments/file-attachment.model";
import { FileAttachmentService } from "ng/src/app/notes/shared/file-attachments/file-attachment.service";
import { catchError, filter, startWith, switchMap } from "rxjs/operators";
import { HttpErrorResponse } from "@angular/common/http";
import { FileDetailType } from "ng/src/app/files/file-details/file-details.model";
import { ImageViewerService } from "ng/src/app/core/image-viewer/image-viewer.service";

@Injectable({
	providedIn: 'root'
})
export class PreviewViewerService {
	
	private readonly previewFileSubject = new Subject<FileAttachment | null>();
	readonly previewFile$ = this.previewFileSubject.asObservable();
	
	private readonly previewFileIdSubject = new Subject<number>();
	private readonly previewFileHrefSubject = new Subject<string>();
	
	constructor(
		private fileService: FileAttachmentService,
		private imageViewerService: ImageViewerService,
		private ngZone: NgZone
	) {
		const self = this;
		
		this.previewFileIdSubject.pipe(
			switchMap(id => fetchAttachment(id))
		)
			.subscribe(fa => this.previewFile(fa!));
		
		this.previewFileHrefSubject.pipe(
			switchMap(href => fetchAttachment(href))
		)
			.subscribe(fa => this.previewFile(fa!));
		
		(window as any).previewFile = function(fileId: number, event?: MouseEvent) {
			if (event?.metaKey || event?.ctrlKey)
				return;
			
			event?.preventDefault();
			ngZone.run(() => {
				self.previewFileId(fileId);
			})
		}
		
		function fetchAttachment(byValue: string | number) {
			return fileService.getFileAttachment(byValue as any)
				.pipe(
					catchError((err: HttpErrorResponse) => {
						console.error(err);
						return EMPTY.pipe(startWith(null));
					}),
					filter(fa => !!fa)
				)
		}
	}
	
	previewFile(file: FileAttachment | null) {
		if (file && FileDetailType.getType(file?.contentType ?? '') === FileDetailType.Type.IMAGE)
			this.imageViewerService.showImageViewer(FileAttachment.getHref(file), [file]);
		else
			this.previewFileSubject.next(file);
	}
	
	previewFileId(id: number) {
		this.previewFileIdSubject.next(id);
	}
	
	previewFileHref(href: string) {
		this.previewFileHrefSubject.next(href);
	}
	
	previewFileEventHandler(file: FileAttachment, event: MouseEvent) {
		if (event.ctrlKey || event.metaKey) {
			return;
		}
		event.preventDefault();
		this.previewFile(file);
	}
	
	static canPreviewFile(fileAttachment: FileAttachment) {
		const contentType = fileAttachment.contentType ?? '';
		const type = FileDetailType.getType(contentType);
		return (type === FileDetailType.Type.PDF || type === FileDetailType.Type.IMAGE || type === FileDetailType.Type.PLAIN_TEXT || FileDetailType.canPlayType(contentType));
	}
	
	static previewInNewWindow(fileAttachment: FileAttachment, startTime?: number) {
		if (this.canPreviewFile(fileAttachment)) {
			const type = FileDetailType.getType(fileAttachment.contentType ?? '');
			let url =`/api/files/${fileAttachment.id}?inline=true`;
			if (typeof startTime === 'number' && (type === FileDetailType.Type.VIDEO || type === FileDetailType.Type.AUDIO)) {
				url = `${url}#t=${startTime}`
			}
			window.open(url, '_blank');
			return true;
		}
		return false;
	}
	
	static getPreviewIcon(type: FileDetailType.Type): string {
		const iconOf: Partial<Record<FileDetailType.Type, string>> = {
			[FileDetailType.Type.PLAIN_TEXT]: 'pi-eye',
			[FileDetailType.Type.IMAGE]: 'pi-image',
			[FileDetailType.Type.PDF]: 'pi-file-pdf',
			[FileDetailType.Type.VIDEO]: 'pi-video',
			[FileDetailType.Type.AUDIO]: 'pi-volume-up'
		}
		
		return iconOf[type] ?? '';
	}
}